import { useRef, useState } from "react";
import { useSelector } from "react-redux";

import {
  filterLegalEntityName,
  filterNameInput,
  handlePasteWithoutAccents,
} from "utils/inputFilters";
import { validateName } from "utils/validators/tenantFormValidators";

import { useCreateLandlord } from "hooks/useLandlords";

import { Landlord } from "js/components/types";
import { Button } from "js/components/ui/button";
import { EmailInput, EmailInputRef } from "js/components/ui/email-input";
import { Input } from "js/components/ui/input";
import { Label } from "js/components/ui/label";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "js/components/ui/sheet";

interface CreateLandlordFormProps {
  children: React.ReactNode;
  onLandlordCreated: (landlord: Landlord) => void;
}

interface FormData {
  name: string;
  first_surname: string;
  second_surname: string;
  email: string;
  phone_number: string;
  legal_entity_name: string;
}

interface FormErrors {
  [key: string]: string;
}

const normalizeString = (value: string): string => {
  if (value === "") {
    return value;
  }
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

export const CreateLandlordForm = ({
  children,
  onLandlordCreated,
}: CreateLandlordFormProps) => {
  const [open, setOpen] = useState(false);
  const [legalType, setLegalType] = useState<"pf" | "pm">("pf");
  const [formData, setFormData] = useState<FormData>({
    name: "",
    first_surname: "",
    second_surname: "",
    email: "",
    phone_number: "",
    legal_entity_name: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const emailInputRef = useRef<EmailInputRef>(null);

  const { userData } = useSelector((state: any) => state.entities.user);

  const createLandlordMutation = useCreateLandlord();

  const validateForm = async (): Promise<boolean> => {
    const newErrors: FormErrors = {};

    // Validate name - matching original validator
    if (!formData.name.trim()) {
      newErrors.name = "Este campo es requerido";
    } else {
      try {
        await validateName(null, formData.name);
      } catch (error: any) {
        newErrors.name = error.message;
      }
    }

    // Validate first surname - matching original validator
    if (!formData.first_surname.trim()) {
      newErrors.first_surname = "Este campo es requerido";
    } else {
      try {
        await validateName(null, formData.first_surname);
      } catch (error: any) {
        newErrors.first_surname = error.message;
      }
    }

    // Validate second surname (optional) - matching original validator
    if (formData.second_surname.trim()) {
      try {
        await validateName(null, formData.second_surname);
      } catch (error: any) {
        newErrors.second_surname = error.message;
      }
    }

    // Email validation is handled by EmailInput component

    // Validate phone number - matching original pattern
    if (!formData.phone_number.trim()) {
      newErrors.phone_number = "Este campo es requerido";
    } else if (!/^[0-9]{10}$/.test(formData.phone_number)) {
      newErrors.phone_number = "El teléfono debe tener 10 dígitos numéricos";
    }

    // Validate legal entity name for Persona Moral
    if (legalType === "pm" && !formData.legal_entity_name.trim()) {
      newErrors.legal_entity_name = "Razón social es requerida";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    // Validate email using EmailInput ref
    const emailIsValid = await emailInputRef.current?.validate();
    if (!emailIsValid) {
      return;
    }

    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    // Match the exact payload structure used in LandlordsDashboard.tsx
    // Note: dial_code is NOT included in the original implementation
    const landlord: any = {
      name: normalizeString(formData.name),
      first_surname: normalizeString(formData.first_surname),
      email: normalizeString(formData.email),
      phone_number: formData.phone_number,
      legal_type: legalType === "pm" ? 2 : 1,
      ...(formData.second_surname && {
        second_surname: normalizeString(formData.second_surname),
      }),
      ...(formData.legal_entity_name && {
        legal_entity_name: normalizeString(formData.legal_entity_name),
      }),
    };

    createLandlordMutation.mutate(landlord, {
      onSuccess: (landlordValues) => {
        onLandlordCreated(landlordValues);
        setOpen(false);
        resetForm();
      },
      onError: (error) => {
        console.error("Error creating landlord:", error);
        setErrors({
          submit: "Error al crear el propietario. Inténtalo de nuevo.",
        });
      },
    });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      first_surname: "",
      second_surname: "",
      email: "",
      phone_number: "",
      legal_entity_name: "",
    });
    setErrors({});
    setLegalType("pf");
  };

  const updateFormData = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Crear Nuevo Propietario</SheetTitle>
          <SheetDescription>
            Completa la información del propietario para agregarlo al sistema.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 px-4 py-4">
          {/* Legal Type Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-700">
              Tipo de persona *
            </Label>
            <div className="grid grid-cols-1 gap-3">
              <div
                onClick={() => setLegalType("pf")}
                className={`border rounded-lg p-3 cursor-pointer transition-all ${
                  legalType === "pf"
                    ? "border-gray-900 bg-gray-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      legalType === "pf" ? "border-gray-900" : "border-gray-300"
                    }`}
                  >
                    {legalType === "pf" && (
                      <div className="w-2 h-2 rounded-full bg-gray-900" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Persona Física
                    </p>
                    <p className="text-xs text-gray-500">
                      Individuos con RFC e identificación
                    </p>
                  </div>
                </div>
              </div>
              <div
                onClick={() => setLegalType("pm")}
                className={`border rounded-lg p-3 cursor-pointer transition-all ${
                  legalType === "pm"
                    ? "border-gray-900 bg-gray-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      legalType === "pm" ? "border-gray-900" : "border-gray-300"
                    }`}
                  >
                    {legalType === "pm" && (
                      <div className="w-2 h-2 rounded-full bg-gray-900" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Persona Moral
                    </p>
                    <p className="text-xs text-gray-500">
                      Empresas o entidades legales
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Legal Entity Name (only for Persona Moral) */}
          {legalType === "pm" && (
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-1 block">
                Razón Social *
              </Label>
              <Input
                value={formData.legal_entity_name}
                onChange={(e) => {
                  const filtered = filterLegalEntityName(e.target.value);
                  updateFormData("legal_entity_name", filtered.toUpperCase());
                }}
                onPaste={(e) => {
                  handlePasteWithoutAccents(
                    e,
                    filterLegalEntityName,
                    (value) => {
                      updateFormData("legal_entity_name", value.toUpperCase());
                    },
                  );
                }}
                placeholder="Razón Social"
                className={`border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${
                  errors.legal_entity_name ? "border-red-500" : ""
                }`}
                maxLength={120}
              />
              {errors.legal_entity_name && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.legal_entity_name}
                </p>
              )}
            </div>
          )}

          {/* Personal Information */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-1 block">
                {legalType === "pm"
                  ? "Nombre del representante legal *"
                  : "Nombre *"}
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => {
                  const filtered = filterNameInput(e.target.value);
                  updateFormData("name", filtered);
                }}
                onPaste={(e) => {
                  handlePasteWithoutAccents(e, filterNameInput, (value) => {
                    updateFormData("name", value);
                  });
                }}
                placeholder="Nombre"
                className={`border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${
                  errors.name ? "border-red-500" : ""
                }`}
              />
              {errors.name && (
                <p className="text-xs text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-700 mb-1 block">
                {legalType === "pm"
                  ? "Primer Apellido del representante *"
                  : "Primer Apellido *"}
              </Label>
              <Input
                value={formData.first_surname}
                onChange={(e) => {
                  const filtered = filterNameInput(e.target.value);
                  updateFormData("first_surname", filtered);
                }}
                onPaste={(e) => {
                  handlePasteWithoutAccents(e, filterNameInput, (value) => {
                    updateFormData("first_surname", value);
                  });
                }}
                placeholder="Primer Apellido"
                className={`border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${
                  errors.first_surname ? "border-red-500" : ""
                }`}
              />
              {errors.first_surname && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.first_surname}
                </p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-700 mb-1 block">
                {legalType === "pm"
                  ? "Segundo Apellido del representante"
                  : "Segundo Apellido"}
              </Label>
              <Input
                value={formData.second_surname}
                onChange={(e) => {
                  const filtered = filterNameInput(e.target.value);
                  updateFormData("second_surname", filtered);
                }}
                onPaste={(e) => {
                  handlePasteWithoutAccents(e, filterNameInput, (value) => {
                    updateFormData("second_surname", value);
                  });
                }}
                placeholder="Segundo Apellido (opcional)"
                className="border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
              />
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-700 mb-1 block">
                {legalType === "pm"
                  ? "Teléfono del representante *"
                  : "Teléfono *"}
              </Label>
              <Input
                value={formData.phone_number}
                onChange={(e) =>
                  updateFormData(
                    "phone_number",
                    e.target.value.replace(/\D/g, ""),
                  )
                }
                placeholder="Teléfono (10 dígitos)"
                maxLength={10}
                className={`border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${
                  errors.phone_number ? "border-red-500" : ""
                }`}
              />
              {errors.phone_number && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.phone_number}
                </p>
              )}
            </div>

            <div>
              <EmailInput
                ref={emailInputRef}
                value={formData.email}
                onChange={(value) => updateFormData("email", value)}
                label={
                  legalType === "pm"
                    ? "Correo del representante"
                    : "Correo electrónico"
                }
                placeholder="correo@ejemplo.com"
                required
                userData={userData}
                showIcon
                showStatus
              />
            </div>
          </div>

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}
        </div>

        <SheetFooter className="gap-2 px-6">
          <SheetClose asChild>
            <Button
              variant="outline"
              disabled={createLandlordMutation.isPending}
            >
              Cancelar
            </Button>
          </SheetClose>
          <Button
            onClick={handleSubmit}
            disabled={createLandlordMutation.isPending}
            className="bg-gray-900 hover:bg-gray-800 text-white"
          >
            {createLandlordMutation.isPending
              ? "Registrando..."
              : "Registrar Propietario"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
